import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Mail, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface OTPVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  taxId?: string;
  businessName?: string;
  businessAddress?: string;
  onSuccess: () => void;
}

const OTPVerificationModal = ({
  isOpen,
  onClose,
  email,
  password,
  firstName,
  lastName,
  phone,
  taxId,
  businessName,
  businessAddress,
  onSuccess,
}: OTPVerificationModalProps) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [verified, setVerified] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);

  // Auto-send code when modal opens
  useEffect(() => {
    if (isOpen && email) {
      sendVerificationCode();
    }
  }, [isOpen, email]);

  // Countdown timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (countdown > 0 && isOpen) {
      interval = setInterval(() => {
        setCountdown(countdown - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [countdown, isOpen]);

  const sendVerificationCode = async () => {
    setIsSendingCode(true);
    try {
      const { error } = await supabase.functions.invoke('send-verification-code', {
        body: {
          email,
          type: 'send'
        }
      });

      if (error) {
        console.error('Error sending verification code:', error);
        toast.error("שגיאה בשליחת קוד", {
          description: "לא ניתן לשלוח קוד אימות. נסה שוב מאוחר יותר",
        });
        return;
      }

      setCountdown(60);
      toast.success("קוד נשלח בהצלחה", {
        description: `קוד אימות נשלח אל ${email}`,
      });
    } catch (error) {
      console.error('Error sending verification code:', error);
      toast.error("שגיאה בשליחת קוד", {
        description: "אירעה שגיאה בשליחת קוד האימות",
      });
    } finally {
      setIsSendingCode(false);
    }
  };

  const verifyCode = async () => {
    setIsLoading(true);
    try {
      const cleanCode = verificationCode.trim().replace(/\s/g, '');

      const { data, error } = await supabase.functions.invoke('send-verification-code', {
        body: {
          email: email.trim(),
          code: cleanCode,
          password,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phone: phone?.trim() || undefined,
          taxId: taxId?.trim() || undefined,
          businessName: businessName?.trim() || undefined,
          businessAddress: businessAddress?.trim() || undefined,
          type: 'verify'
        }
      });

      if (error || !data.success) {
        toast.error("קוד שגוי", {
          description: "הקוד שהזנת אינו נכון או שפג תוקפו. בדוק ונסה שוב",
        });
        return;
      }

      setVerified(true);
      toast.success("חשבון נוצר בהצלחה!", {
        description: "החשבון שלך נוצר ואומת. מתחבר...",
      });

      // Sign in the user
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (signInError) {
        console.error('Error signing in after verification:', signInError);
        toast.error("שגיאה בהתחברות", {
          description: "החשבון נוצר אך אירעה שגיאה בהתחברות",
        });
        return;
      }

      // Wait a moment then call success callback
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (error) {
      console.error('Error verifying code:', error);
      toast.error("שגיאה באימות", {
        description: "אירעה שגיאה באימות הקוד",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = () => {
    if (countdown > 0) return;
    sendVerificationCode();
  };

  const handleCloseAttempt = () => {
    if (!verified) {
      setShowExitConfirmation(true);
    } else {
      onClose();
    }
  };

  if (verified) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md" dir="rtl">
          <div className="text-center py-8">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-green-800 mb-2">חשבון נוצר בהצלחה!</h3>
            <p className="text-green-700">החשבון שלך נוצר ואומת בהצלחה</p>
            <div className="flex justify-center mt-4">
              <Loader2 className="w-6 h-6 animate-spin text-green-600" />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleCloseAttempt()}>
        <DialogContent
          className="max-w-md w-[95vw] sm:w-full"
          dir="rtl"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-8 h-8 text-primary" />
            </div>
            <DialogTitle className="text-xl text-center">אימות כתובת אימייל</DialogTitle>
            <DialogDescription className="text-right">
              קוד אימות נשלח אל {email}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">

            <div>
              <label className="text-sm font-medium block mb-2">
                הזן קוד אימות
              </label>
              <Input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="text-center text-lg tracking-widest"
                maxLength={6}
                disabled={isLoading}
              />
            </div>

            <Button
              onClick={verifyCode}
              disabled={isLoading || verificationCode.length !== 6}
              className="w-full"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  יוצר חשבון...
                </>
              ) : 'צור חשבון'}
            </Button>

            <div className="text-center text-sm text-muted-foreground">
              לא קיבלת קוד?{' '}
              <Button
                variant="link"
                onClick={handleResendCode}
                disabled={countdown > 0 || isSendingCode}
                className="p-0 h-auto text-sm"
              >
                {countdown > 0 ? `שלח שוב בעוד ${countdown} שניות` : 'שלח שוב'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showExitConfirmation} onOpenChange={setShowExitConfirmation}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              האם את/ה בטוח/ה?
            </AlertDialogTitle>
            <AlertDialogDescription>
              יציאה בשלב זה תבטל את תהליך האימות ותצטרך/י להתחיל מחדש. כל המידע שהזנת בשלב ההרשמה יישמר כטיוטה, אך החשבון לא ייווצר עד להשלמת האימות.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <AlertDialogCancel className="mt-0">המשך באימות</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowExitConfirmation(false);
                onClose();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              צא בכל זאת
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default OTPVerificationModal;
